import { type ComponentProps, useState } from "react";

import { Icons, type InputText } from "@fielmann-ag/prisma-components";

import { useI18n } from "@/i18n";

type InputAction = NonNullable<
  ComponentProps<typeof InputText>["inputActions"]
>[number];

interface PasswordMasking {
  /** Native input type: `"password"` masks the value, `"text"` reveals it. */
  inputType: "text" | "password";
  inputActions: InputAction[];
}

/**
 * Show/hide masking for a Prisma `InputText` password field.
 *
 * Pass `inputType` as `type` and `inputActions` to `InputText`. Each call owns
 * its own state, so password and password-repeat toggle independently.
 */
export function usePasswordMasking(): PasswordMasking {
  const { messages } = useI18n();
  const [isRevealed, setIsRevealed] = useState(false);

  return {
    inputType: isRevealed ? "text" : "password",
    inputActions: [
      {
        id: "toggle-password-masking",
        kind: "iconButton",
        // The label already names the next action, so `aria-pressed` would
        // announce the state twice ("Hide password, pressed").
        label: isRevealed
          ? messages.shared.password.hide
          : messages.shared.password.show,
        variant: "plain",
        icon: isRevealed ? (
          <Icons.Medium.EyeClosed aria-hidden="true" />
        ) : (
          <Icons.Medium.Eye aria-hidden="true" />
        ),
        onPress: () => setIsRevealed((previous) => !previous),
      },
    ],
  };
}
